import React, { ReactNode, useCallback, useMemo, useState } from 'react';
import { Box } from 'apps/sigma2-ui/src/components/ui/Box';
import { ButtonGroup } from 'apps/sigma2-ui/src/components/ui/ButtonGroup';
import { ButtonGroupOption } from 'apps/sigma2-ui/src/components/ui/ButtonGroup';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { FiltersFeatureName } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { getSelectedSingleDateFromStore } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { mapDateRangeToPreferredViewByOption } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { getViewByValueFromStore } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { getSelectedDateRangeFromStore } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { PrimaryFilterSelection } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { FocusArea } from 'libs/end-to-end/src/focus-area';
import { FilterStoreHook } from 'libs/end-to-end/src/filters-v2/types/filters-v2.types';
import { parse } from 'date-fns';
import { DateRange } from 'libs/end-to-end/src/date-range';
import { getDateRangeForPeriod } from 'libs/end-to-end/src/date-range';
import { Period } from 'libs/end-to-end/src/date-range';
import { EVENT_STUDY_DATE_FORMAT_PATTERN } from 'libs/end-to-end/src/progress';
import { S2DateRangePicker } from 'apps/sigma2-ui/src/components/DateRangePicker';

interface PeriodOption {
  label: ReactNode;
  value: Period;
}

export const DateRangeMenu = ({
  feature,
  organizationId,
  databaseRevisionId,
  databaseId,
  store,
  focusArea,
  analysisName,
  closeMenu,
  setPrimaryFilters,
}: {
  feature: FiltersFeatureName;
  organizationId: string;
  databaseRevisionId: string;
  databaseId: string;
  store: FilterStoreHook;
  focusArea: FocusArea | null;
  analysisName: string | undefined;
  closeMenu: () => void;
  setPrimaryFilters: (params: {
    selections: PrimaryFilterSelection[];
    shouldPreventAutoSubmit?: boolean;
  }) => void;
}) => {
  const treatmentDate = useMemo(() => {
    if (feature !== 'timeSeriesTrendsPrimary') {
      return;
    }

    const treatmentDateRaw =
      getSelectedSingleDateFromStore({
        feature,
        organizationId,
        databaseRevisionId,
        databaseId,
        store,
      }) ?? undefined;

    return treatmentDateRaw
      ? parse(treatmentDateRaw, EVENT_STUDY_DATE_FORMAT_PATTERN, new Date())
      : undefined;
  }, [databaseId, databaseRevisionId, feature, organizationId, store]);

  const persistedDateRange = useMemo(
    () =>
      getSelectedDateRangeFromStore({
        feature,
        organizationId,
        databaseRevisionId,
        databaseId,
        store,
        focusArea,
        analysisName,
      }),
    [
      databaseId,
      databaseRevisionId,
      feature,
      focusArea,
      analysisName,
      organizationId,
      store,
    ],
  );

  const getUpdatedViewByPrimaryFilterSelectionForRange = useCallback(
    (range: DateRange): PrimaryFilterSelection[] => {
      const mappedViewByOpt = mapDateRangeToPreferredViewByOption(range);

      const viewByStoreValue = getViewByValueFromStore({
        store,
        feature,
        focusArea,
        analysisName,
        databaseId,
        databaseRevisionId,
        organizationId,
      });

      if (viewByStoreValue === mappedViewByOpt) {
        return [];
      }

      return [
        {
          primaryFilterKey: 'viewBy',
          value: mappedViewByOpt,
        },
      ];
    },
    [
      databaseId,
      databaseRevisionId,
      feature,
      focusArea,
      analysisName,
      organizationId,
      store,
    ],
  );

  const [dateRangePickerOpen, setDateRangePickerOpen] = useState(false);

  const onDateRangePickerCommit = useCallback(
    ({ start, end }: DateRange) => {
      if (!start || !end) {
        const periodDateRange = getDateRangeForPeriod({
          period: 'custom',
          anchor: treatmentDate,
          anchorPosition: treatmentDate ? 'center' : 'end',
        });

        setPrimaryFilters({
          selections: [
            {
              primaryFilterKey: 'dateRange',
              value: {
                startDate: periodDateRange.start,
                endDate: periodDateRange.end,
                dateRangeType: 'custom',
              },
            },
          ],
        });
      } else {
        setPrimaryFilters({
          selections: [
            ...getUpdatedViewByPrimaryFilterSelectionForRange({ start, end }),
            {
              primaryFilterKey: 'dateRange',
              value: {
                startDate: start,
                endDate: end,
                dateRangeType: 'custom',
              },
            },
          ],
        });
      }

      closeMenu();
    },
    [
      closeMenu,
      getUpdatedViewByPrimaryFilterSelectionForRange,
      setPrimaryFilters,
      treatmentDate,
    ],
  );

  const periodOptions: PeriodOption[] = useMemo(
    () => [
      { label: 'All', value: 'all' },
      { label: '7D', value: '1w' },
      { label: '28D', value: '28d' },
      { label: '3M', value: '3m' },
      { label: '6M', value: '6m' },
      { label: '1YR', value: '1y' },
      { label: '2YR', value: '2y' },
      { label: '3YR', value: '3y' },
      {
        label: (
          <Spacing
            size={'sm'}
            direction={'horizontal'}
            className="items-center"
          >
            <span>Custom</span>
            {persistedDateRange?.dateRangeType === 'custom' && (
              <S2DateRangePicker
                initialValue={{
                  start: persistedDateRange.startDate,
                  end: persistedDateRange.endDate,
                }}
                onCommit={onDateRangePickerCommit}
                open={dateRangePickerOpen}
                onOpenChange={setDateRangePickerOpen}
              />
            )}
          </Spacing>
        ),
        value: 'custom',
      },
    ],
    [
      dateRangePickerOpen,
      onDateRangePickerCommit,
      persistedDateRange?.dateRangeType,
      persistedDateRange?.endDate,
      persistedDateRange?.startDate,
    ],
  );

  const buttonOptions: ButtonGroupOption[] = useMemo(
    () =>
      periodOptions.map((option) => ({
        text: option.label,
        value: option.value,
        selected: option.value === persistedDateRange?.dateRangeType,
      })),
    [persistedDateRange?.dateRangeType, periodOptions],
  );

  return (
    <Box as={'div'}>
      <Spacing size={'sm'} direction={'vertical'}>
        <ButtonGroup
          initialSelectedIndex={buttonOptions.findIndex(
            (opt) => opt.value === persistedDateRange?.dateRangeType,
          )}
          options={buttonOptions}
          onChange={(value) => {
            const periodTyped = Period.safeParse(value);
            if (!periodTyped.success) {
              closeMenu();
              return;
            }

            if (value === persistedDateRange?.dateRangeType) {
              closeMenu();
              return;
            }

            const periodDateRange = getDateRangeForPeriod({
              period: periodTyped.data,
              anchor: treatmentDate,
              anchorPosition: treatmentDate ? 'center' : 'end',
            });
            const startDate = periodDateRange.start;
            const endDate = periodDateRange.end;

            setPrimaryFilters({
              selections: [
                ...getUpdatedViewByPrimaryFilterSelectionForRange({
                  start: startDate,
                  end: endDate,
                }),
                {
                  primaryFilterKey: 'dateRange',
                  value: {
                    startDate,
                    endDate,
                    dateRangeType: value,
                  },
                },
              ],
              shouldPreventAutoSubmit: value === 'custom',
            });

            setDateRangePickerOpen(true);
            closeMenu();
          }}
        />
      </Spacing>
    </Box>
  );
};
